import { Slot } from '@radix-ui/react-slot';
import type { ReactNode } from 'react';
import * as React from 'react';
import type {
  ControllerProps,
  FieldPath,
  FieldValues,
  UseFormReturn,
} from 'react-hook-form';
import { Controller, FormProvider } from 'react-hook-form';
import type { z } from 'zod';

import {
  FormFieldContext,
  useFormField,
} from '@/components/primitives/useFormField';
import { TransText } from '@/i18n/trans/text';
import { cn } from '@/utils/cn';
import { Label, typographyVariants } from '@packages/webshop-design-system';
import _kebabCase from 'lodash/kebabCase';

interface FormProps<T extends z.ZodType>
  extends React.FormHTMLAttributes<HTMLFormElement> {
  form: UseFormReturn<z.infer<T>>;
  onSubmit: (data: z.infer<T>) => void | Promise<void>;
}

function Form<T extends z.ZodType>({
  form,
  onSubmit,
  children,
  className,
  ...props
}: FormProps<T>) {
  return (
    <FormProvider {...form}>
      {/* noValidate: zod owns validation; native constraint validation (e.g.
          type="email") would otherwise block submit without visible errors */}
      <form
        onSubmit={form.handleSubmit(onSubmit)}
        className={className}
        noValidate
        {...props}
      >
        {children}
      </form>
    </FormProvider>
  );
}

const FormField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  ...props
}: ControllerProps<TFieldValues, TName>) => {
  return (
    <FormFieldContext.Provider value={{ name: props.name }}>
      <Controller {...props} />
    </FormFieldContext.Provider>
  );
};

const FormItem = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => {
  return <div ref={ref} className={className} {...props} />;
});
FormItem.displayName = 'FormItem';

interface FormLabelProps extends React.ComponentPropsWithoutRef<'label'> {
  required?: boolean;
}

const FormLabel = React.forwardRef<HTMLLabelElement, FormLabelProps>(
  ({ className, children, required, ...props }, ref) => {
    const { error, formItemId } = useFormField();

    return (
      <Label
        ref={ref}
        id={props.id ?? `${formItemId}-label`}
        className={cn(
          typographyVariants({
            variant: 'body2',
            className: 'flex flex-wrap gap-0.5 text-neutral',
          }),
          className,
          {
            ['text-error-text']: Boolean(error),
          }
        )}
        htmlFor={formItemId}
        {...props}
      >
        {children}
        {!required && (
          <span>
            (<TransText i18nKey="optional" />)
          </span>
        )}
      </Label>
    );
  }
);
FormLabel.displayName = 'FormLabel';

const FormControl = React.forwardRef<
  React.ElementRef<typeof Slot>,
  React.ComponentPropsWithoutRef<typeof Slot> & {
    label: ReactNode;
    hideLabel?: boolean;
    required?: boolean;
  }
>(({ label, hideLabel, required, ...props }, ref) => {
  const { error, formItemId, formDescriptionId, formMessageId, name } =
    useFormField();

  return (
    <>
      <FormLabel
        className={cn('mb-1', { 'sr-only': hideLabel })}
        required={required}
      >
        {label}
      </FormLabel>
      <Slot
        ref={ref}
        id={formItemId}
        aria-describedby={
          !error ? formDescriptionId : `${formDescriptionId} ${formMessageId}`
        }
        aria-invalid={!!error}
        {...props}
      />
      <FormMessage
        className="mt-1"
        data-testid={`${_kebabCase(name)}-input-error`}
      />
    </>
  );
});
FormControl.displayName = 'FormControl';

const FormDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => {
  const { formDescriptionId } = useFormField();

  return (
    <p
      ref={ref}
      id={formDescriptionId}
      className={cn(
        typographyVariants({
          variant: 'label',
          className: 'text-neutral-light',
        }),
        className
      )}
      {...props}
    />
  );
});
FormDescription.displayName = 'FormDescription';

const FormMessage = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, children, ...props }, ref) => {
  const { error, formMessageId } = useFormField();
  const body = error ? String(error?.message) : children;

  if (!body) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={formMessageId}
      className={cn(
        typographyVariants({
          variant: 'label',
          className: 'text-neutral',
        }),
        className,
        { 'text-error-text': Boolean(error) }
      )}
      {...props}
    >
      {body}
    </p>
  );
});
FormMessage.displayName = 'FormMessage';

export {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
};
